import React, { useEffect, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';

const NewFeatureModal = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [step, setStep] = useState(0); // 0: initial, 1: scanning, 2: content
    const { t, language } = useLanguage();
    const navigate = useNavigate();
    const location = useLocation();

    const storageKey = 'hasSeenZeroDayMethod_v1';

    useEffect(() => {
        if (location.pathname.includes('/secret')) return;
        const hasSeen = localStorage.getItem(storageKey);
        if (!hasSeen) {
            // Появление отключено по запросу (код модалки сохранён в компоненте)
            /*
            const timer = setTimeout(() => {
                setIsOpen(true);
            }, 2000);
            return () => clearTimeout(timer);
            */
        }
    }, [location.pathname]);

    useEffect(() => {
        if (isOpen) {
            // Sequence of "hacking" animation steps
            const t1 = setTimeout(() => setStep(1), 500);
            const t2 = setTimeout(() => setStep(2), 2500);
            return () => {
                clearTimeout(t1);
                clearTimeout(t2);
            };
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        setIsOpen(false);
        localStorage.setItem(storageKey, 'true');
    }, []);

    const handleTryNow = () => {
        handleClose();
        navigate(language === 'en' ? '/en/secret' : '/secret');
    };

    const terminalLines = [
        "Initializing kernel...",
        "Scanning for exploits...",
        "Bypassing network bridge...",
        "Accessing Zero-Day database...",
        "SUCCESS: 0-day vulnerability found!"
    ];

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md overflow-hidden">
                    {/* Background Glitch Effect */}
                    <div className="absolute inset-0 opacity-20 pointer-events-none overflow-hidden">
                        <div className="absolute inset-0 bg-grid-pattern bg-[length:50px_50px]"></div>
                        <motion.div
                            animate={{ y: ["-100%", "100%"] }}
                            transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-0 bg-gradient-to-b from-transparent via-accent-blue/20 to-transparent h-20 w-full"
                        />
                    </div>

                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, rotateX: 20 }}
                        animate={{ opacity: 1, scale: 1, rotateX: 0 }}
                        exit={{ opacity: 0, scale: 1.1, filter: "blur(10px)" }}
                        className="bg-dark-900 border-2 border-accent-blue/50 rounded-lg max-w-lg w-full shadow-[0_0_50px_rgba(59,130,246,0.2)] overflow-hidden relative"
                    >
                        {/* Terminal Header */}
                        <div className="bg-dark-800 px-4 py-2 border-b border-white/10 flex items-center justify-between">
                            <div className="flex gap-1.5">
                                <div className="w-3 h-3 rounded-full bg-red-500/50"></div>
                                <div className="w-3 h-3 rounded-full bg-yellow-500/50"></div>
                                <div className="w-3 h-3 rounded-full bg-green-500/50"></div>
                            </div>
                            <span className="text-[10px] font-mono text-gray-500 tracking-widest uppercase">Encryption: AES-256</span>
                            <button onClick={handleClose} className="text-gray-500 hover:text-white transition-colors">
                                <i className="fas fa-times text-sm"></i>
                            </button>
                        </div>

                        <div className="p-8 relative">
                            {step < 2 ? (
                                <div className="font-mono text-sm space-y-2">
                                    {terminalLines.map((line, i) => (
                                        <motion.div
                                            key={i}
                                            initial={{ opacity: 0, x: -10 }}
                                            animate={step >= 1 ? { opacity: 1, x: 0 } : {}}
                                            transition={{ delay: i * 0.3 }}
                                            className={i === 4 ? "text-accent-green font-bold" : "text-accent-blue"}
                                        >
                                            <span className="text-gray-600 mr-2">[{new Date().toLocaleTimeString()}]</span>
                                            {line}
                                        </motion.div>
                                    ))}
                                    <motion.span
                                        animate={{ opacity: [1, 0] }}
                                        transition={{ duration: 0.8, repeat: Infinity }}
                                        className="inline-block w-2 h-4 bg-accent-blue align-middle ml-1"
                                    />
                                </div>
                            ) : (
                                <motion.div
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    className="flex flex-col items-center text-center"
                                >
                                    <div className="relative mb-6">
                                        <motion.div
                                            animate={{
                                                scale: [1, 1.1, 1],
                                                rotate: [0, 5, -5, 0]
                                            }}
                                            transition={{ duration: 4, repeat: Infinity }}
                                            className="w-24 h-24 bg-accent-blue/20 rounded-xl flex items-center justify-center border-2 border-accent-blue shadow-[0_0_30px_rgba(59,130,246,0.4)]"
                                        >
                                            <i className="fas fa-microchip text-5xl text-accent-blue"></i>
                                        </motion.div>
                                        <div className="absolute -top-2 -right-2 bg-accent-red text-white text-[10px] font-bold px-2 py-0.5 rounded shadow-lg animate-pulse">
                                            NEW METHOD
                                        </div>
                                    </div>

                                    <h2 className="text-3xl font-black text-white mb-4 tracking-tighter uppercase italic">
                                        {t('newFeatureTitle')}
                                    </h2>

                                    <div className="h-1 w-24 bg-gradient-to-r from-transparent via-accent-blue to-transparent mb-6"></div>

                                    <p className="text-gray-400 mb-8 leading-relaxed font-medium">
                                        {t('newFeatureDescription')}
                                    </p>

                                    <div className="grid grid-cols-1 gap-4 w-full">
                                        <button
                                            onClick={handleTryNow}
                                            className="group relative w-full py-4 bg-accent-blue text-white rounded font-black transition-all overflow-hidden"
                                        >
                                            <div className="absolute inset-0 w-full h-full bg-gradient-to-r from-white/0 via-white/20 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
                                            <span className="relative flex items-center justify-center gap-3 tracking-widest">
                                                <i className="fas fa-terminal text-lg"></i>
                                                {t('newFeatureTryBtn')}
                                            </span>
                                        </button>
                                        <button
                                            onClick={handleClose}
                                            className="text-gray-500 hover:text-gray-300 text-xs font-mono uppercase tracking-[0.2em] transition-colors"
                                        >
                                            Ignore notification
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default NewFeatureModal;